/**
 * HeroCollage — Mobile hero collage matching the FoodPhotos reference.
 *
 * Layout is a vertical stack occupying the RIGHT ~45% of the hero.
 * Cards are absolutely positioned within a container that takes the full
 * height of the parent (which is the hero absolute wrapper).
 *
 * Card order (top → bottom):
 *   • Fries        — small landscape, top
 *   • Thali        — dominant LARGE card, tilted, center (overlaps headline)
 *   • Milkshake    — portrait, mid-left of collage
 *   • Manchurian   — square, mid-right of collage
 *   • Kosha        — landscape, bottom
 */
import { memo } from "react";

const IMG = {
  thali: "https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/cmz3xnc1_Fam%27s%20Favorite%20Matar%20Paneer%20-%20Feast%20Thali.jpg",
  milkshake: "https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/vlpfisf1_Chocolate%20Fudge%20Milkshake.jpg",
  fries: "https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/3qrsgneu_Peri%20Peri%20Fries%20with%20Cheese.jpg",
  manchurian: "https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/1qh0o3wv_Dry%20Manchurian.png",
  kosha: "https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/a5i71tnq_Chicken%20Kosha.png",
};

// Cards positioned in percentages of the parent container (relative to
// the whole hero right-column). Extends over the full hero height.
const CARDS = [
  // TOP — loaded fries
  { key: "fries", src: IMG.fries, alt: "Loaded Peri Peri Fries",
    style: { top: "0%", right: "-4%", width: "78%", height: "13%" },
    float: "float-a", z: 20, rounded: "rounded-2xl",
    objectPosition: "50% 45%" },
  // CENTER HERO — Thali (dominant)
  { key: "thali", src: IMG.thali, alt: "North Indian Thali",
    style: { top: "14%", right: "-2%", width: "104%", height: "36%" },
    float: "float-b", z: 30, rounded: "rounded-[18px]",
    objectPosition: "50% 55%" },
  // MID-LEFT — milkshake portrait
  { key: "milkshake", src: IMG.milkshake, alt: "Chocolate Fudge Milkshake",
    style: { top: "54%", left: "-6%", width: "40%", height: "24%" },
    float: "float-c", z: 22, rounded: "rounded-2xl",
    objectPosition: "50% 50%" },
  // MID-RIGHT — manchurian square
  { key: "manchurian", src: IMG.manchurian, alt: "Dry Manchurian",
    style: { top: "53%", right: "-4%", width: "58%", height: "22%" },
    float: "float-d", z: 24, rounded: "rounded-2xl",
    objectPosition: "50% 50%" },
  // BOTTOM — chicken kosha landscape
  { key: "kosha", src: IMG.kosha, alt: "Chicken Kosha",
    style: { top: "78%", right: "2%", width: "70%", height: "20%" },
    float: "float-e", z: 26, rounded: "rounded-2xl",
    objectPosition: "50% 50%" },
];

export const HeroCollage = memo(function HeroCollage() {
  return (
    <div className="hero-collage relative w-full h-full" data-testid="hero-collage" aria-hidden="true">
      {/* Small orange sparkle marks near thali top-left (matches reference) */}
      <svg
        className="absolute z-40 pointer-events-none"
        style={{ top: "13%", left: "-4%", width: "22px", height: "22px" }}
        viewBox="0 0 24 24" fill="none" aria-hidden="true"
      >
        <path d="M6 4 L7 10 M12 2 L11 8 M17 5 L15 10" stroke="#F97316" strokeWidth="1.8" strokeLinecap="round" />
      </svg>

      {CARDS.map((card) => (
        <div
          key={card.key}
          className={`hero-collage-card absolute overflow-hidden ${card.rounded} ${card.float}`}
          style={{ ...card.style, zIndex: card.z }}
          data-testid={`collage-card-${card.key}`}
        >
          <img
            src={card.src}
            alt={card.alt}
            className="w-full h-full object-cover select-none"
            style={{ objectPosition: card.objectPosition }}
            draggable={false}
            loading="eager"
          />
        </div>
      ))}
    </div>
  );
});
